import { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Chip, Stack, Divider, TextField } from '@mui/material';
import SectionLabel from 'features/IRWorkstation/components/SectionLabel';
import PrimaryButton from 'features/IRWorkstation/components/Common/Buttons/PrimaryButton';

const MAX_CUSTOM_INSTRUCTION_LENGTH = 200;

// Options within a group are mutually exclusive
const PRESET_GROUPS = [
  [
    { value: 'shorter', label: 'Shorten' },
    { value: 'expand', label: 'Expand' },
  ],
  [
    { value: 'warmer', label: 'Warmer' },
    { value: 'more_formal', label: 'More formal' },
  ],
];

const QuickEditsForm = ({ handleApplyEdits, disabled, loading }) => {
  const [presetTypes, setPresetTypes] = useState([]);
  const [customInstruction, setCustomInstruction] = useState('');

  const trimmedInstruction = customInstruction.trim();
  const isCustomInstructionValid = trimmedInstruction.length <= MAX_CUSTOM_INSTRUCTION_LENGTH;
  const hasChanges = presetTypes.length > 0 || trimmedInstruction.length > 0;

  const togglePreset = (selected, type) => {
    if (selected.includes(type)) return selected.filter((t) => t !== type);

    const group = PRESET_GROUPS.find((g) => g.some((o) => o.value === type));
    const siblings = group.map((o) => o.value);
    return [...selected.filter((t) => !siblings.includes(t)), type];
  };

  // Presets and custom instruction are mutually exclusive
  const handlePresetToggle = (type) => {
    setPresetTypes((prev) => togglePreset(prev, type));
    setCustomInstruction('');
  };

  const handleCustomInstructionChange = (value) => {
    setCustomInstruction(value);
    if (value.trim()) setPresetTypes([]);
  };

  const handleApply = async () => {
    handleApplyEdits({
      presetTypes,
      customInstruction: trimmedInstruction || undefined,
    });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack spacing={2}>
        <SectionLabel text='Quick Edits' />

        <Box display='flex' gap={2} flexWrap='wrap'>
          {PRESET_GROUPS.map((group, i) => (
            <Box key={i} display='contents'>
              {i > 0 && <Divider orientation='vertical' flexItem />}
              {group.map(({ value, label }) => {
                const selected = presetTypes.includes(value);
                return (
                  <Chip
                    key={value}
                    label={label}
                    onClick={() => handlePresetToggle(value)}
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    size='small'
                    disabled={disabled}
                  />
                );
              })}
            </Box>
          ))}
        </Box>

        <Divider>
          <Typography variant='body2'>OR</Typography>
        </Divider>

        <TextField
          label='Describe a specific edit'
          value={customInstruction}
          onChange={(e) => handleCustomInstructionChange(e.target.value)}
          size='small'
          slotProps={{
            htmlInput: {
              maxLength: MAX_CUSTOM_INSTRUCTION_LENGTH + 1,
              'data-testid': 'custom-instruction-input',
            },
          }}
          disabled={disabled}
          fullWidth
          error={!isCustomInstructionValid}
          helperText={
            isCustomInstructionValid
              ? `${trimmedInstruction.length} / ${MAX_CUSTOM_INSTRUCTION_LENGTH}`
              : `Instruction cannot exceed ${MAX_CUSTOM_INSTRUCTION_LENGTH} characters`
          }
        />
      </Stack>

      <Box display='flex' justifyContent='flex-end'>
        <PrimaryButton
          onClick={handleApply}
          loading={loading}
          label='Apply'
          disabled={!hasChanges || !isCustomInstructionValid || disabled}
        />
      </Box>
    </Box>
  );
};

QuickEditsForm.propTypes = {
  handleApplyEdits: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

export default QuickEditsForm;